import React, { useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo, faSearch, faSpinner, faTriangleExclamation, faUsers } from '@fortawesome/free-solid-svg-icons'
import {
  applyEmailSignatures,
  fetchWorkspaceDirectoryMerge,
  type SignatureApplyResult,
  type SignatureRecipientRow,
} from '../../services/prService'
import type { ContactEntry } from '../../services/profileService'
import { displayEmail, displayPhone } from '../../utils/contactDisplay'
import {
  buildSignatureHtml,
  DEFAULT_SIGNATURE_FIELDS,
  DEFAULT_SIGNATURE_HTML,
  parseSignatureFields,
  signatureMatchesFields,
  type EmailSignatureFields,
} from '../../utils/emailSignatureTemplate'

type SignatureEditorTab = 'edit' | 'advanced'
function displayContactName(c: ContactEntry): string {
  if (c.preferredName && c.name) {
    const rest = c.name.split(' ').slice(1).join(' ')
    return rest ? `${c.preferredName} ${rest}` : c.preferredName
  }
  return c.name || displayEmail(c) || c.employeeId
}

function previewHtml(template: string, sample: ContactEntry | null): string {
  // Keep placeholder behavior aligned with backend signatureTemplateMerge utility.
  const name = sample ? displayContactName(sample) : 'Jordan Smith'
  const position = sample?.position || 'Field Technician'
  const email = sample ? displayEmail(sample) || 'jordan.smith@example.com' : 'jordan.smith@example.com'
  const phone = sample ? displayPhone(sample) || '(555) 010-0199' : '(555) 010-0199'
  const department = sample?.department || 'Operations'
  return template
    .replace(/\{\{\s*name\s*\}\}/gi, name)
    .replace(/\{\{\s*position\s*\}\}/gi, position)
    .replace(/\{\{\s*email\s*\}\}/gi, email)
    .replace(/\{\{\s*phone\s*\}\}/gi, phone)
    .replace(/\{\{\s*department\s*\}\}/gi, department)
}

/** Preview sample for Workspace-only rows (no Portal contact). */
function rowToPreviewContact(row: SignatureRecipientRow): ContactEntry | null {
  if (row.contact) return row.contact
  if (!row.email) return null
  return {
    employeeId: row.rowKey,
    name: row.displayName,
    preferredName: null,
    position: '',
    phone: '',
    email: row.email,
    workEmail: row.email,
    workPhone: '',
    department: '',
    supervisor: '',
  }
}

function rowSourceHint(row: SignatureRecipientRow): string {
  const hasC = row.sources.includes('contact')
  const hasW = row.sources.includes('workspace')
  if (hasC && hasW) return 'Portal + Workspace'
  if (hasW) return 'Workspace only'
  return 'Portal only'
}

function rowDisabledHint(row: SignatureRecipientRow): string | null {
  if (row.selectable) return null
  switch (row.unselectableReason) {
    case 'contact_only':
      return 'No matching Workspace user for this email.'
    case 'no_email':
      return 'No email on file.'
    case 'workspace_suspended':
      return 'Workspace account suspended.'
    case 'contact_duplicate_email':
      return 'Another contact already matched this Workspace email.'
    default:
      return 'Cannot select for signature apply.'
  }
}

function isWorkspaceUnmatched(row: SignatureRecipientRow): boolean {
  return row.selectable && row.sources.includes('workspace') && !row.matchedToPortal
}

function previewOptionLabel(r: SignatureRecipientRow): string {
  const email = r.email || '—'
  let suffix = ''
  if (r.matchedToPortal) suffix = ' · Portal linked'
  else if (r.sources.includes('workspace')) suffix = ' · Workspace only'
  else suffix = ' · Portal only'
  const base = `${r.displayName} (${email})`
  const full = `${base}${suffix}`
  return full.length > 90 ? `${full.slice(0, 87)}…` : full
}

const EmailSignaturesPanel: React.FC = () => {
  const [rows, setRows] = useState<SignatureRecipientRow[]>([])
  const [mergeMeta, setMergeMeta] = useState<{
    workspaceConfigured: boolean
    workspaceError: string | null
    workspaceUserCount: number
  } | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loadingRecipients, setLoadingRecipients] = useState(true)
  const [search, setSearch] = useState('')
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(new Set())
  /** Empty string = generic placeholder preview (not tied to a recipient row). */
  const [previewSampleRowKey, setPreviewSampleRowKey] = useState('')
  const [signatureTab, setSignatureTab] = useState<SignatureEditorTab>('edit')
  const [signatureFields, setSignatureFields] = useState<EmailSignatureFields>(DEFAULT_SIGNATURE_FIELDS)
  const [signatureHtml, setSignatureHtml] = useState(DEFAULT_SIGNATURE_HTML)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitResult, setSubmitResult] = useState<SignatureApplyResult | null>(null)

  const htmlMatchesEditor = useMemo(
    () => signatureMatchesFields(signatureHtml, signatureFields),
    [signatureHtml, signatureFields],
  )

  const updateSignatureField = <K extends keyof EmailSignatureFields>(key: K, value: EmailSignatureFields[K]) => {
    setSignatureFields((prev) => {
      const next = { ...prev, [key]: value }
      setSignatureHtml(buildSignatureHtml(next))
      return next
    })
  }

  const onAdvancedHtmlChange = (html: string) => {
    setSignatureHtml(html)
    const parsed = parseSignatureFields(html)
    if (parsed) setSignatureFields(parsed)
  }

  const rebuildFromEditor = () => {
    setSignatureHtml(buildSignatureHtml(signatureFields))
  }

  useEffect(() => {
    let cancelled = false
    setLoadingRecipients(true)
    setLoadError(null)
    fetchWorkspaceDirectoryMerge()
      .then((res) => {
        if (cancelled) return
        setRows(res.rows)
        setMergeMeta({
          workspaceConfigured: res.workspaceConfigured,
          workspaceError: res.workspaceError,
          workspaceUserCount: res.workspaceUserCount,
        })
      })
      .catch(() => {
        if (!cancelled) {
          setRows([])
          setMergeMeta(null)
          setLoadError('Could not load recipients. Check that you have PR access and try again.')
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingRecipients(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const selectableRowKeySet = useMemo(() => {
    const s = new Set<string>()
    for (const r of rows) {
      if (r.selectable) s.add(r.rowKey)
    }
    return s
  }, [rows])

  useEffect(() => {
    setSelectedRowKeys((prev) => {
      const next = new Set<string>()
      for (const key of prev) {
        if (selectableRowKeySet.has(key)) next.add(key)
      }
      return next
    })
  }, [selectableRowKeySet])

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) => {
      const blob = [r.displayName, r.email, r.employeeId || '', rowSourceHint(r)].join(' ').toLowerCase()
      return blob.includes(q)
    })
  }, [rows, search])

  /** Preview dropdown: checked recipients only. */
  const previewCandidateRows = useMemo(() => {
    const canPreview = (r: SignatureRecipientRow) => rowToPreviewContact(r) !== null
    return rows.filter((r) => selectedRowKeys.has(r.rowKey) && canPreview(r))
  }, [rows, selectedRowKeys])

  useEffect(() => {
    setPreviewSampleRowKey((prev) => {
      if (previewCandidateRows.length === 0) return ''
      if (prev === '') return ''
      const ok = previewCandidateRows.some((r) => r.rowKey === prev)
      if (ok) return prev
      return previewCandidateRows[0].rowKey
    })
  }, [previewCandidateRows])

  const previewSample = useMemo((): ContactEntry | null => {
    if (!previewSampleRowKey) return null
    const r = rows.find((x) => x.rowKey === previewSampleRowKey)
    return r ? rowToPreviewContact(r) : null
  }, [rows, previewSampleRowKey])

  const toggleOne = (rowKey: string) => {
    if (!selectableRowKeySet.has(rowKey)) return
    setSelectedRowKeys((prev) => {
      const next = new Set(prev)
      if (next.has(rowKey)) next.delete(rowKey)
      else next.add(rowKey)
      return next
    })
  }

  const selectAllFiltered = () => {
    setSelectedRowKeys((prev) => {
      const next = new Set(prev)
      for (const r of filteredRows) {
        if (r.selectable) next.add(r.rowKey)
      }
      return next
    })
  }

  /** Adds selectable rows that match Portal + Workspace (respects current search filter). */
  const selectPortalLinkedFiltered = () => {
    setSelectedRowKeys((prev) => {
      const next = new Set(prev)
      for (const r of filteredRows) {
        if (r.selectable && r.matchedToPortal) next.add(r.rowKey)
      }
      return next
    })
  }

  const portalLinkedInFilterCount = useMemo(
    () => filteredRows.filter((r) => r.selectable && r.matchedToPortal).length,
    [filteredRows],
  )

  const clearSelection = () => setSelectedRowKeys(new Set())
  const canSubmit = selectedRowKeys.size > 0 && !loadingRecipients && !loadError && !isSubmitting
  const selectedRowKeyList = useMemo(() => [...selectedRowKeys], [selectedRowKeys])
  const rowByKey = useMemo(() => new Map(rows.map((r) => [r.rowKey, r])), [rows])

  const submitSignatures = async () => {
    if (!canSubmit) return
    setIsSubmitting(true)
    setSubmitError(null)
    setSubmitResult(null)
    try {
      const result = await applyEmailSignatures({
        templateHtml: signatureHtml,
        rowKeys: selectedRowKeyList,
      })
      setSubmitResult(result)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to submit signature updates.'
      setSubmitError(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="space-y-8 max-w-6xl">
      {mergeMeta && !mergeMeta.workspaceConfigured && (
        <div className="alert alert-warning text-sm">
          <FontAwesomeIcon icon={faCircleInfo} className="shrink-0 opacity-80" />
          <div>
            <span className="font-medium">Workspace Directory not configured</span>
            <span className="text-base-content/80">
              {' '}
              — Set <code className="text-xs bg-base-200 px-1 rounded">GOOGLE_WORKSPACE_*</code> on the server (see{' '}
              <code className="text-xs bg-base-200 px-1 rounded">backend/env.example</code>) to list Workspace users and
              enable matched recipients. Until then, you can still select directory contacts for preview.
            </span>
          </div>
        </div>
      )}

      {mergeMeta?.workspaceConfigured && mergeMeta.workspaceError && (
        <div className="alert alert-error text-sm">
          <span className="font-medium">Could not load Google Workspace users.</span>{' '}
          <span className="opacity-90">{mergeMeta.workspaceError}</span>
        </div>
      )}

      {/* 1. Signature editor */}
      <div className="card bg-base-100 border border-base-200 shadow-md">
        <div className="card-body gap-4">
          <h3 className="card-title text-lg">Signature</h3>

          <div role="tablist" className="tabs tabs-boxed w-fit">
            <button
              type="button"
              role="tab"
              className={`tab ${signatureTab === 'edit' ? 'tab-active' : ''}`}
              aria-selected={signatureTab === 'edit'}
              onClick={() => setSignatureTab('edit')}
              disabled={isSubmitting}
            >
              Edit
            </button>
            <button
              type="button"
              role="tab"
              className={`tab ${signatureTab === 'advanced' ? 'tab-active' : ''}`}
              aria-selected={signatureTab === 'advanced'}
              onClick={() => setSignatureTab('advanced')}
              disabled={isSubmitting}
            >
              Advanced
            </button>
          </div>

          {signatureTab === 'edit' ? (
            <div className="space-y-4">
              <p className="text-sm text-base-content/65">
                Name, title, phone, and email are filled automatically for each recipient (
                <code className="text-xs bg-base-200 px-1 rounded">{'{{name}}'}</code>,{' '}
                <code className="text-xs bg-base-200 px-1 rounded">{'{{position}}'}</code>,{' '}
                <code className="text-xs bg-base-200 px-1 rounded">{'{{phone}}'}</code>,{' '}
                <code className="text-xs bg-base-200 px-1 rounded">{'{{email}}'}</code>
                ). Edit the shared company details below.
              </p>

              {!htmlMatchesEditor && (
                <div className="alert alert-warning text-sm py-3">
                  <FontAwesomeIcon icon={faTriangleExclamation} className="shrink-0" />
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between w-full">
                    <span>
                      This signature was customized in Advanced. Changing a field below will rebuild it using the
                      standard layout.
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-warning shrink-0"
                      onClick={rebuildFromEditor}
                      disabled={isSubmitting}
                    >
                      Rebuild from Edit fields
                    </button>
                  </div>
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="form-control w-full sm:col-span-2">
                  <span className="label py-1">
                    <span className="label-text">Logo image URL</span>
                  </span>
                  <input
                    type="url"
                    className="input input-bordered w-full"
                    value={signatureFields.logoUrl}
                    onChange={(e) => updateSignatureField('logoUrl', e.target.value)}
                    disabled={isSubmitting}
                    aria-label="Logo image URL"
                  />
                </label>

                <label className="form-control w-full sm:col-span-2">
                  <span className="label py-1">
                    <span className="label-text">Logo alt text</span>
                  </span>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    value={signatureFields.logoAlt}
                    onChange={(e) => updateSignatureField('logoAlt', e.target.value)}
                    disabled={isSubmitting}
                    aria-label="Logo alt text"
                  />
                </label>

                <label className="form-control w-full sm:col-span-2">
                  <span className="label py-1">
                    <span className="label-text">Address</span>
                  </span>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    value={signatureFields.address}
                    onChange={(e) => updateSignatureField('address', e.target.value)}
                    disabled={isSubmitting}
                    aria-label="Address"
                  />
                </label>

                <label className="form-control w-full">
                  <span className="label py-1">
                    <span className="label-text">Website URL</span>
                  </span>
                  <input
                    type="url"
                    className="input input-bordered w-full"
                    value={signatureFields.websiteUrl}
                    onChange={(e) => updateSignatureField('websiteUrl', e.target.value)}
                    disabled={isSubmitting}
                    aria-label="Website URL"
                  />
                </label>

                <label className="form-control w-full">
                  <span className="label py-1">
                    <span className="label-text">Website display text</span>
                  </span>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    value={signatureFields.websiteLabel}
                    onChange={(e) => updateSignatureField('websiteLabel', e.target.value)}
                    disabled={isSubmitting}
                    aria-label="Website display text"
                  />
                </label>

                <label className="form-control w-full sm:col-span-2">
                  <span className="label py-1">
                    <span className="label-text">Footer line</span>
                  </span>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    value={signatureFields.footer}
                    onChange={(e) => updateSignatureField('footer', e.target.value)}
                    disabled={isSubmitting}
                    aria-label="Footer line"
                  />
                </label>

                <label className="form-control w-full sm:col-span-2">
                  <span className="label py-1">
                    <span className="label-text">Legal disclaimer</span>
                  </span>
                  <textarea
                    className="textarea textarea-bordered w-full text-sm leading-relaxed min-h-[120px]"
                    value={signatureFields.disclaimer}
                    onChange={(e) => updateSignatureField('disclaimer', e.target.value)}
                    disabled={isSubmitting}
                    aria-label="Legal disclaimer"
                  />
                </label>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-base-content/65">
                Edit the raw HTML template. Placeholders{' '}
                <code className="text-xs bg-base-200 px-1 rounded">{'{{name}}'}</code>,{' '}
                <code className="text-xs bg-base-200 px-1 rounded">{'{{position}}'}</code>,{' '}
                <code className="text-xs bg-base-200 px-1 rounded">{'{{email}}'}</code>,{' '}
                <code className="text-xs bg-base-200 px-1 rounded">{'{{phone}}'}</code>, and{' '}
                <code className="text-xs bg-base-200 px-1 rounded">{'{{department}}'}</code> are filled per person when
                applied.
              </p>
              <textarea
                className="textarea textarea-bordered w-full font-mono text-sm leading-relaxed min-h-[320px]"
                spellCheck={false}
                value={signatureHtml}
                onChange={(e) => onAdvancedHtmlChange(e.target.value)}
                aria-label="Signature HTML"
                disabled={isSubmitting}
              />
            </div>
          )}
        </div>
      </div>

      {/* 2. Preview */}
      <div className="card bg-base-100 border border-base-200 shadow-md">
        <div className="card-body gap-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
            <h3 className="card-title text-lg shrink-0">Preview</h3>
            <label className="form-control w-full sm:max-w-md sm:min-w-[14rem]">
              <select
                className="select select-bordered select-sm w-full"
                value={previewSampleRowKey}
                onChange={(e) => setPreviewSampleRowKey(e.target.value)}
                disabled={loadingRecipients || Boolean(loadError) || isSubmitting}
                aria-label="Choose sample person for signature preview"
              >
                <option value="">Generic placeholder</option>
                {previewCandidateRows.map((r) => (
                  <option key={r.rowKey} value={r.rowKey}>
                    {previewOptionLabel(r)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div
            className="rounded-box border border-dashed border-base-300 bg-base-200/30 p-6 min-h-[140px]"
            dangerouslySetInnerHTML={{ __html: previewHtml(signatureHtml, previewSample) }}
          />
        </div>
      </div>

      {/* 3. Selected users / recipients */}
      <div className="card bg-base-100 border border-base-200 shadow-md">
        <div className="card-body gap-4">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faUsers} className="text-primary" />
            <h3 className="card-title text-lg">Recipients</h3>
          </div>
          <p className="text-sm text-base-content/65 -mt-2">
            Choose Workspace mailboxes to target ({rows.length} rows). Rows that match an LTS Portal contact show{' '}
            <span className="font-medium text-base-content">Portal + Workspace</span>. Workspace-only rows can still be
            selected; they show a warning so you know there is no linked Portal user.
          </p>

          <div className="space-y-3 pt-2">
            {loadingRecipients ? (
              <div className="flex items-center gap-3 text-base-content/70 py-8 justify-center">
                <FontAwesomeIcon icon={faSpinner} spin />
                Loading recipients…
              </div>
            ) : loadError ? (
              <div className="text-error text-sm">{loadError}</div>
            ) : (
              <>
                <label className="input input-bordered flex items-center gap-2 w-full">
                  <FontAwesomeIcon icon={faSearch} className="opacity-50 text-sm" />
                  <input
                    type="search"
                    className="grow min-w-0"
                    placeholder="Search by name, email, ID…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    disabled={isSubmitting}
                  />
                </label>
                <div className="flex flex-wrap gap-2 items-center">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline"
                    onClick={selectAllFiltered}
                    disabled={isSubmitting}
                  >
                    Select all matching (selectable only)
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline btn-success"
                    onClick={selectPortalLinkedFiltered}
                    disabled={portalLinkedInFilterCount === 0 || isSubmitting}
                    title="Add to selection: rows with Portal + Workspace match (same filter as list below)"
                  >
                    Portal-linked{portalLinkedInFilterCount > 0 ? ` (${portalLinkedInFilterCount})` : ''}
                  </button>
                  <button type="button" className="btn btn-sm btn-ghost" onClick={clearSelection} disabled={isSubmitting}>
                    Clear selection
                  </button>
                  <span className="text-sm text-base-content/60 self-center">{selectedRowKeys.size} selected</span>
                </div>
                <ul className="rounded-box border border-base-200 max-h-72 overflow-y-auto divide-y divide-base-200 bg-base-100">
                  {filteredRows.length === 0 ? (
                    <li className="p-4 text-sm text-base-content/60 text-center">No rows match your search.</li>
                  ) : (
                    filteredRows.map((r) => {
                      const disabled = !r.selectable
                      const checked = selectedRowKeys.has(r.rowKey)
                      const warnUnmatched = isWorkspaceUnmatched(r)
                      return (
                        <li key={r.rowKey}>
                          <div
                            className={`flex items-start gap-3 p-3 ${
                              disabled ? 'opacity-50 bg-base-200/20' : 'hover:bg-base-200/40'
                            } ${warnUnmatched ? 'border-l-4 border-l-warning bg-warning/5' : ''}`}
                          >
                            <input
                              type="checkbox"
                              className="checkbox checkbox-sm checkbox-primary mt-0.5 shrink-0 disabled:opacity-40"
                              checked={checked}
                              disabled={disabled || isSubmitting}
                              onChange={() => toggleOne(r.rowKey)}
                              aria-label={`Select ${r.displayName}`}
                            />
                            <span className="min-w-0 flex-1">
                              <span className="flex flex-wrap items-center gap-2 gap-y-1">
                                <span className="font-medium truncate">{r.displayName}</span>
                                {r.matchedToPortal && (
                                  <span className="badge badge-success badge-sm shrink-0 font-normal">Portal linked</span>
                                )}
                                {warnUnmatched && (
                                  <span className="badge badge-warning badge-sm shrink-0 gap-1 font-normal">
                                    <FontAwesomeIcon icon={faTriangleExclamation} className="text-[0.65rem]" />
                                    Not in Portal directory
                                  </span>
                                )}
                              </span>
                              <span className="text-xs text-base-content/55 block truncate mt-0.5">
                                {r.email || '—'} · {rowSourceHint(r)}
                              </span>
                              {warnUnmatched && (
                                <span className="text-xs text-warning-content/90 block mt-1">
                                  No LTS Portal contact uses this email — signature can still be applied to this
                                  Workspace mailbox when enabled; placeholders use Workspace name only.
                                </span>
                              )}
                              {disabled && rowDisabledHint(r) && (
                                <span className="text-xs text-base-content/45 block mt-0.5">{rowDisabledHint(r)}</span>
                              )}
                            </span>
                          </div>
                        </li>
                      )
                    })
                  )}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 4. Submit */}
      <div className="card bg-base-100 border border-base-200 shadow-md">
        <div className="card-body gap-4">
          <h3 className="card-title text-lg">Submit</h3>
          <p className="text-sm text-base-content/70 -mt-2">
            <strong className="text-base-content">Selected recipients:</strong>{' '}
            {selectedRowKeys.size === 0
              ? 'No one selected yet — pick at least one selectable row above.'
              : `${selectedRowKeys.size} recipient${selectedRowKeys.size === 1 ? '' : 's'} will receive this signature.`}
          </p>
          <div className="card-actions justify-end">
            <button
              type="button"
              className="btn btn-primary btn-wide"
              disabled={!canSubmit}
              onClick={submitSignatures}
            >
              {isSubmitting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                  Submitting…
                </>
              ) : (
                'Submit'
              )}
            </button>
          </div>
          {submitError && (
            <div className="alert alert-error text-sm">
              <span>{submitError}</span>
            </div>
          )}
          {submitResult && (
            <div className={`alert text-sm ${submitResult.summary.failed ? 'alert-warning' : 'alert-success'}`}>
              <div className="space-y-1">
                <div>
                  Signature apply finished: {submitResult.summary.ok} succeeded, {submitResult.summary.failed} failed.
                </div>
                {submitResult.summary.failed > 0 && (
                  <ul className="list-disc pl-5">
                    {submitResult.results
                      .filter((r) => !r.ok)
                      .map((r) => {
                        const row = rowByKey.get(r.rowKey)
                        return (
                          <li key={`${r.rowKey}:${r.email}`}>
                            {(row?.displayName || r.email || r.rowKey) + ': ' + (r.error || 'Unknown error')}
                          </li>
                        )
                      })}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default EmailSignaturesPanel
